import { create } from "zustand";
import { persist, createJSONStorage, devtools } from "zustand/middleware";
import { DEFAULT_LANGUAGE } from "@/config";
import type { UserInfoProps, CreationProps } from "@/pages/Home/index.d";
import Api from "@/service";
import { getEnvConfig } from "@/utils/getEnv";

// 支持的语言类型
export type Language = "zh-TW" | "en" | "th" | "pt" | "hi" | "es";

// 用户登录状态接口
interface AuthState {
  isLoggedIn: boolean;
  token: string | null;
}

// 用户信息状态接口
interface UserInfoState {
  userInfo: UserInfoProps;
  award: any[];
  creationList: CreationProps[];
  shareCode: string;
}

// 加载状态接口
interface LoadingState {
  isLoading: boolean;
  loadingText?: string;
}

// 语言状态接口
interface LanguageState {
  currentLanguage: Language;
}

// Popup 状态接口
interface PopupState {
  popupInfo: {
    type: "gameLogin" | "share" | "secretCodeLogin" | "award";
    open: boolean;
  };
}

// 统一的全局状态接口
interface GlobalState
  extends AuthState,
    UserInfoState,
    LoadingState,
    LanguageState,
    PopupState {
  // 认证相关方法
  login: (token: string) => void;
  logout: () => void;

  // 异步认证方法
  handleLogin: () => Promise<void>;
  handleLogout: () => Promise<void>;

  // 用户信息相关方法
  setUserInfo: (userInfo: UserInfoProps) => void;
  setAward: (award: any[]) => void;
  setCreationList: (creationList: CreationProps[]) => void;
  setShareCode: (shareCode: string) => void;
  fetchCreationList: () => Promise<void>;
  fetchShareCode: () => Promise<void>;
  fetchUserInfo: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;

  // 加载状态相关方法
  setLoading: (loading: boolean, text?: string) => void;
  showLoading: (text?: string) => void;
  hideLoading: () => void;

  // 语言相关方法
  setLanguage: (lang: Language) => void;

  // Popup 相关方法
  setPopupInfo: (popupInfo: PopupState["popupInfo"]) => void;
  openPopup: (type: PopupState["popupInfo"]["type"]) => void;
  closePopup: () => void;

  // 工具方法
  reset: () => void;
  initialize: () => Promise<void>;
}

// 创建统一的全局状态管理
export const useGlobalStore = create<GlobalState>()(
  devtools(
    persist(
      (set, get) => ({
        // 认证状态
        isLoggedIn: false,
        token: null,

        // 用户信息状态
        userInfo: {
          vote_ticket: 0,
          use_ticket: 0,
          use_share_code: 0,
          invite_user: 0,
          join_community_count: 0,
          active_count: 0,
          regression_level: 0,
          new_level: 0,
        },
        award: [],
        creationList: [],
        shareCode: "",

        // 加载状态
        isLoading: false,
        loadingText: undefined,

        // 语言状态
        currentLanguage: DEFAULT_LANGUAGE,

        // Popup 状态
        popupInfo: {
          type: "gameLogin",
          open: false,
        },

        // 认证相关方法
        login: (token: string) => {
          set({
            isLoggedIn: true,
            token,
          });
        },

        logout: () => {
          set({
            isLoggedIn: false,
            token: null,
            userInfo: {} as UserInfoProps,
            award: [],
            shareCode: "",
          });
        },

        // 用户信息相关方法
        setUserInfo: (userInfo: UserInfoProps) => set({ userInfo }),
        setAward: (award: any[]) => set({ award }),
        setCreationList: (creationList: CreationProps[]) =>
          set({ creationList }),
        setShareCode: (shareCode: string) => set({ shareCode }),

        // 获取分享码
        fetchShareCode: async () => {
          if (get().shareCode) {
            return;
          }
          const { setShareCode } = get();
          const res = await Api.getShareCode();
          if (res.code === 0) {
            setShareCode(res.data.code);
          }
        },

        // 获取作品列表
        fetchCreationList: async () => {
          const { setCreationList } = get();
          const res = await Api.getCreationList();
          setCreationList(res.data.creation_list);
        },

        // 获取用户信息
        fetchUserInfo: async () => {
          const { setUserInfo, token } = get();
          try {
            const res = await Api.getUserInfo();
            if (res.code === 0) {
              setUserInfo(res.data.vote_info);
              // token已经在store中管理，无需重复设置
            }
          } catch (error) {
            console.error("获取用户信息失败:", error);
          }
        },

        // 刷新用户信息（投票后调用）
        refreshUserInfo: async () => {
          await get().fetchUserInfo();
        },

        // 加载状态相关方法
        setLoading: (loading: boolean, text?: string) =>
          set({ isLoading: loading, loadingText: text }),
        showLoading: (text?: string) =>
          set({ isLoading: true, loadingText: text }),
        hideLoading: () => set({ isLoading: false, loadingText: undefined }),

        // 语言相关方法
        setLanguage: (lang: Language) => set({ currentLanguage: lang }),

        // 异步认证方法
        handleLogin: async () => {
          const { fetchUserInfo, openPopup, token } = get();
          try {
            // 本地有Token，说明登录过，直接使用token获取个人信息
            if (token) {
              await fetchUserInfo();
              return;
            }

            // 如果没有token，根据环境决定登录方式
            if (getEnvConfig().env === "hmt") {
              // 打开游戏登录弹窗
              openPopup("gameLogin");
            } else {
              openPopup("secretCodeLogin");
            }
          } catch (error) {
            console.error("登录失败:", error);
          }
        },

        handleLogout: async () => {
          const { logout } = get();

          try {
            logout();
          } catch (error) {}
        },

        // Popup 相关方法
        setPopupInfo: (popupInfo: PopupState["popupInfo"]) =>
          set({ popupInfo }),
        openPopup: (type: PopupState["popupInfo"]["type"]) =>
          set({ popupInfo: { type, open: true } }),
        closePopup: () =>
          set((state) => ({ popupInfo: { ...state.popupInfo, open: false } })),

        // 工具方法
        reset: () => {
          set({
            isLoggedIn: false,
            token: null,
            userInfo: {
              vote_ticket: 0,
              use_ticket: 0,
              use_share_code: 0,
              invite_user: 0,
              join_community_count: 0,
              active_count: 0,
              regression_level: 0,
              new_level: 0,
            },
            award: [],
            creationList: [],
            isLoading: false,
            loadingText: undefined,
            currentLanguage: DEFAULT_LANGUAGE,
            popupInfo: {
              type: "gameLogin",
              open: false,
            },
          });
        },

        // 初始化方法 - 页面加载时调用
        initialize: async () => {
          const { fetchCreationList, fetchShareCode, token, fetchUserInfo } =
            get();
          try {
            // 获取作品列表（无论是否登录都需要）
            await fetchCreationList();

            // 如果有token，获取用户信息
            if (token) {
              await fetchUserInfo();
            }
          } catch (error) {
            console.error("初始化失败:", error);
          }
        },
      }),
      {
        name: "global-store", // 本地存储的 key
        storage: createJSONStorage(() => localStorage),
        // 只持久化需要持久化的状态
        partialize: (state) => ({
          isLoggedIn: state.isLoggedIn,
          token: state.token,

          userInfo: state.userInfo,
          award: state.award,
          creationList: state.creationList,
          currentLanguage: state.currentLanguage,
        }),
      }
    ),
    {
      name: "global-store", // DevTools 中显示的名称
    }
  )
);

// 为了向后兼容，导出原有的store hooks
// 这些hooks现在基于统一的全局状态
export const useUserInfoStore = () => {
  const store = useGlobalStore();
  return {
    userInfo: store.userInfo,
    award: store.award,
    creationList: store.creationList,
    shareCode: store.shareCode,
    setShareCode: store.setShareCode,
    fetchShareCode: store.fetchShareCode,
    setUserInfo: store.setUserInfo,
    setAward: store.setAward,
    setCreationList: store.setCreationList,
    fetchCreationList: store.fetchCreationList,
    fetchUserInfo: store.fetchUserInfo,
    refreshUserInfo: store.refreshUserInfo,
  };
};

export const useLoadingStore = () => {
  const store = useGlobalStore();
  return {
    isLoading: store.isLoading,
    loadingText: store.loadingText,
    setLoading: store.setLoading,
    showLoading: store.showLoading,
    hideLoading: store.hideLoading,
  };
};

export const useLanguageStore = () => {
  const store = useGlobalStore();
  return {
    currentLanguage: store.currentLanguage,
    setLanguage: store.setLanguage,
  };
};

// 新增的认证状态管理hook
export const useAuthStore = () => {
  const store = useGlobalStore();
  return {
    isLoggedIn: store.isLoggedIn,
    token: store.token,
    login: store.login,
    logout: store.logout,
    handleLogin: store.handleLogin,
    handleLogout: store.handleLogout,
  };
};

// 新增的 Popup 状态管理hook
export const usePopupStore = () => {
  const store = useGlobalStore();
  return {
    popupInfo: store.popupInfo,
    setPopupInfo: store.setPopupInfo,
    openPopup: store.openPopup,
    closePopup: store.closePopup,
  };
};

// 导出类型定义
export type {
  AuthState,
  UserInfoState,
  LoadingState,
  LanguageState,
  PopupState,
  GlobalState,
};
